import type { RoundName } from "../../domain/round/types";

const API_BASE = String(import.meta.env.VITE_API_BASE_URL || "/api").replace(/\/$/, "");

export type ScheduleStatus = "scheduled" | "pending" | "postponed" | "suspended" | "completed";
export type TimingSource = "dpworld" | "emergency" | "manual";

export type DpWorldDiagnostics = {
  complete: boolean;
  groupCount: number;
  expectedCount: number | null;
  playerGroups: number;
  playerCount: number;
  missingPlayerGroups: number[];
  teeCounts: Record<string, number>;
  sessionCounts: Record<string, number>;
  statusCounts?: Record<string, number>;
  scheduledCount?: number;
  postponedCount?: number;
  duplicateSlots: string[];
  duplicateGroups: number[];
  malformedTimeGroups: number[];
  sourceMode: string;
  parserVersion: string;
  warnings: string[];
};

export type DpWorldGroup = Record<string, unknown> & {
  group?: number;
  tee?: number;
  start?: string;
  startTime?: string;
  time?: string;
  finish9?: string;
  finish18?: string;
  session?: string;
  players?: Array<Record<string, unknown>>;
  scheduleStatus?: ScheduleStatus;
  timingSource?: TimingSource;
};

export type DpWorldPreview = {
  roundName: RoundName;
  sourceUrl: string;
  rawText: string;
  groups: DpWorldGroup[];
  count: number;
  diagnostics: DpWorldDiagnostics;
  cachedAt?: string;
  emergency?: boolean;
};

export type DpWorldNetworkDiagnostics = {
  roundName: RoundName;
  sourceUrl: string;
  parserVersion: string;
  responses: Array<{
    mode?: string;
    status?: number;
    finalUrl?: string;
    contentType?: string;
    contentLength?: number;
    title?: string;
    sources?: Array<Record<string, unknown>>;
    error?: string;
  }>;
};

async function post<T>(path: string, payload: Record<string, unknown>): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const detail = body?.detail;
    const message = typeof detail === "string" ? detail : detail?.message;
    throw new Error(message || `DPWT: HTTP ${response.status}`);
  }
  return body as T;
}

export function fetchDpWorldPreview(url: string, roundName: RoundName, expectedCount = 0) {
  return post<DpWorldPreview>("/generator/tee-times/dpworld/preview", { url, roundName, expectedCount });
}

export function diagnoseDpWorld(url: string, roundName: RoundName) {
  return post<DpWorldNetworkDiagnostics>("/generator/tee-times/dpworld/diagnose", { url, roundName, expectedCount: 0 });
}
